import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import {
  ArrowLeft, Save, Loader2, Plus, Trash2, ExternalLink, Link2, Sparkles,
  History, RotateCcw, Upload, Eye,
} from "lucide-react";

const STATUS_LABEL = {
  draft: "Brouillon", scheduled: "Programmé", published: "Publié", archived: "Archivé",
};

export default function AdminContentEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [template, setTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const dirty = useRef(false);
  const timer = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/content/${id}`);
        setItem(data);
        const { data: t } = await api.get(`/admin/content-templates/${data.content_type}`);
        setTemplate(t);
      } catch { toast.error("Contenu introuvable."); navigate("/admin/content"); }
    })();
  }, [id, navigate]);

  const save = useCallback(async (data) => {
    setSaving(true);
    try {
      const payload = {
        title: data.title, slug: data.slug, excerpt: data.excerpt,
        cover_image: data.cover_image, cover_alt: data.cover_alt,
        featured: data.featured, parent_id: data.parent_id || null,
        fields: data.fields, sections: data.sections, seo: data.seo,
        schema_types: data.schema_types, tag_ids: data.tag_ids,
        keyword_ids: data.keyword_ids, internal_links: data.internal_links,
      };
      const { data: updated } = await api.patch(`/admin/content/${id}`, payload);
      dirty.current = false;
      setSavedAt(new Date());
      setItem((cur) => ({ ...updated, __v: (cur?.__v || 0) })); // keep server truth (slug may change)
    } catch { toast.error("Échec de l'enregistrement."); }
    finally { setSaving(false); }
  }, [id]);

  const [visualsBusy, setVisualsBusy] = useState(false);
  const generateVisuals = async () => {
    setVisualsBusy(true);
    toast.info("Génération des visuels IA lancée — comptez 1 à 3 minutes…");
    try {
      const { data } = await api.post(`/admin/content/${id}/generate-visuals`, { illustrations: 2 });
      const result = await new Promise((resolve, reject) => {
        const iv = setInterval(async () => {
          try {
            const { data: j } = await api.get(`/admin/content/visuals-job/${data.job_id}`);
            if (j.status === "done") { clearInterval(iv); resolve(j.result); }
            else if (j.status === "error") { clearInterval(iv); reject(new Error(j.error || "Erreur")); }
          } catch (e) { clearInterval(iv); reject(e); }
        }, 3000);
      });
      const { data: fresh } = await api.get(`/admin/content/${id}`);
      setItem(() => ({ ...fresh }));
      toast.success(`Visuels générés : couverture + ${result.illustrations} illustration(s) + variantes réseaux sociaux.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Génération des visuels impossible.");
    } finally { setVisualsBusy(false); }
  };


  const set = (patch) => {
    setItem((cur) => {
      const next = { ...cur, ...patch };
      dirty.current = true;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => save(next), 1200);
      return next;
    });
  };

  const setStatus = async (status, scheduled_at) => {
    try {
      const { data } = await api.post(`/admin/content/${id}/status`, { status, scheduled_at });
      setItem((cur) => ({ ...cur, status: data.status, published_at: data.published_at, scheduled_at: data.scheduled_at }));
      toast.success(`Statut : ${STATUS_LABEL[status]}.`);
    } catch { toast.error("Changement de statut impossible."); }
  };

  if (!item) return <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#96836B]" /></div>;

  const prefix = template?.url_prefix || "content";

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Nav />
      <main className="pt-28 pb-24 px-6 md:px-12 max-w-[1400px] mx-auto">
        <div className="flex items-center justify-between gap-4 flex-wrap border-b border-[#EAE8E3] pb-6">
          <div className="flex items-center gap-4 min-w-0">
            <button onClick={() => navigate("/admin/content")} className="text-[#737373] hover:text-[#1A1B18]"><ArrowLeft className="h-5 w-5" /></button>
            <div className="min-w-0">
              <p className="label-eyebrow">{template?.name || item.content_type}</p>
              <input
                value={item.title || ""}
                onChange={(e) => set({ title: e.target.value })}
                data-testid="content-title"
                className="font-serif-display text-3xl md:text-4xl bg-transparent border-none outline-none w-full"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-[#737373]">
              {saving ? "Enregistrement…" : savedAt ? `Enregistré ${format(savedAt, "HH:mm:ss")}` : ""}
            </span>
            <Button onClick={generateVisuals} disabled={visualsBusy} variant="outline" className="rounded-none text-[11px] uppercase tracking-[0.2em] border-[#96836B] text-[#1A1B18] hover:bg-[#96836B] hover:text-white" data-testid="btn-generate-visuals">
              {visualsBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />} Visuels IA
            </Button>
            {item.status === "published" && (
              <a href={`/${prefix}/${item.slug}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-[#737373] hover:text-[#1A1B18]" data-testid="btn-view-public"><Eye className="h-4 w-4" /> Voir</a>
            )}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
          {/* Main editor */}
          <div>
            <Tabs defaultValue="content">
              <TabsList className="rounded-none bg-transparent border-b border-[#EAE8E3] w-full justify-start gap-6 p-0 h-auto">
                {[["content", "Contenu"], ["seo", "SEO"], ["mesh", "Maillage"], ["settings", "Réglages"]].map(([v, l]) => (
                  <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#1A1B18] data-[state=active]:shadow-none bg-transparent px-0 pb-3 text-sm">{l}</TabsTrigger>
                ))}
              </TabsList>

              {/* CONTENT */}
              <TabsContent value="content" className="mt-8 space-y-8">
                <CoverUploader item={item} set={set} />
                {!["destination", "pays", "region"].includes(item.content_type) && <ParentPicker item={item} set={set} />}
                <Field label="Extrait (résumé)"><Textarea value={item.excerpt || ""} onChange={(e) => set({ excerpt: e.target.value })} className="luxury-input min-h-[70px]" placeholder="Court résumé affiché dans les listes et le SEO." data-testid="content-excerpt" /></Field>

                {template?.fields_schema?.length > 0 && (
                  <div className="bg-white border border-[#EAE8E3] p-6 space-y-4">
                    <p className="label-eyebrow">Caractéristiques</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {template.fields_schema.map((f) => (
                        <Field key={f.key} label={f.label} help={f.help}>
                          {f.type === "textarea"
                            ? <Textarea value={item.fields?.[f.key] || ""} onChange={(e) => set({ fields: { ...item.fields, [f.key]: e.target.value } })} className="luxury-input min-h-[60px]" />
                            : f.type === "select"
                            ? <select value={item.fields?.[f.key] || ""} onChange={(e) => set({ fields: { ...item.fields, [f.key]: e.target.value } })} data-testid={`field-${f.key}`} className="w-full border border-[#EAE8E3] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#1A1B18]">
                                <option value="">— Choisir —</option>
                                {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                              </select>
                            : f.type === "tags"
                            ? <div className="flex flex-wrap gap-2" data-testid={`field-${f.key}`}>
                                {(f.options || []).map((o) => {
                                  const sel = (item.fields?.[f.key] || []).includes(o);
                                  return (
                                    <button type="button" key={o}
                                      onClick={() => {
                                        const cur = item.fields?.[f.key] || [];
                                        const next = sel ? cur.filter((x) => x !== o) : [...cur, o];
                                        set({ fields: { ...item.fields, [f.key]: next } });
                                      }}
                                      className={`text-[11px] uppercase tracking-[0.14em] px-3 py-1.5 border transition-colors ${sel ? "bg-[#1A1B18] text-white border-[#1A1B18]" : "border-[#D9D5CC] text-[#737373] hover:border-[#1A1B18]"}`}>
                                      {o}
                                    </button>
                                  );
                                })}
                              </div>
                            : <Input type={f.type === "number" ? "number" : "text"} value={item.fields?.[f.key] || ""} onChange={(e) => set({ fields: { ...item.fields, [f.key]: e.target.value } })} className="luxury-input" data-testid={`field-${f.key}`} />}
                        </Field>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  <p className="label-eyebrow">Sections</p>
                  {(item.sections || []).map((s, idx) => (
                    <SectionEditor key={s.id || idx} section={s}
                      onChange={(patch) => { const sections = [...item.sections]; sections[idx] = { ...s, ...patch }; set({ sections }); }} />
                  ))}
                </div>
              </TabsContent>

              {/* SEO */}
              <TabsContent value="seo" className="mt-8 space-y-6">
                <SeoPanel item={item} set={set} prefix={prefix} />
              </TabsContent>

              {/* MESH / internal linking */}
              <TabsContent value="mesh" className="mt-8">
                <MeshPanel id={id} item={item} set={set} />
              </TabsContent>

              {/* SETTINGS */}
              <TabsContent value="settings" className="mt-8 space-y-6">
                <Field label="Types de données structurées (Schema.org)">
                  <Input value={(item.schema_types || []).join(", ")} onChange={(e) => set({ schema_types: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} className="luxury-input" placeholder="Article, FAQPage, BreadcrumbList" data-testid="schema-types" />
                </Field>
                <VersionsPanel id={id} onRestored={(d) => setItem(d)} />
              </TabsContent>
            </Tabs>
          </div>

          {/* Publication rail */}
          <aside className="space-y-6">
            <div className="bg-white border border-[#EAE8E3] p-6 space-y-4" data-testid="publish-panel">
              <p className="label-eyebrow">Publication</p>
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#737373]">Statut actuel :</span>
                <span className="text-sm font-medium">{STATUS_LABEL[item.status]}</span>
              </div>
              {item.status !== "published"
                ? <Button onClick={() => setStatus("published")} data-testid="btn-publish" className="w-full rounded-none bg-[#1A1B18] hover:bg-[#96836B] text-white text-[11px] uppercase tracking-[0.24em] py-5">Publier maintenant</Button>
                : <Button onClick={() => setStatus("draft")} data-testid="btn-unpublish" variant="outline" className="w-full rounded-none text-[11px] uppercase tracking-[0.24em] py-5">Repasser en brouillon</Button>}
              <div className="border-t border-[#F2EFE9] pt-4 space-y-2">
                <Label className="label-eyebrow">Programmer</Label>
                <ScheduleControl current={item.scheduled_at} onSchedule={(dt) => setStatus("scheduled", dt)} />
              </div>
              <button onClick={() => setStatus("archived")} className="text-[11px] uppercase tracking-[0.2em] text-[#737373] hover:text-[#7A4B3B]">Archiver</button>
            </div>

            <div className="bg-white border border-[#EAE8E3] p-6 space-y-3" data-testid="featured-panel">
              <p className="label-eyebrow">Mise en avant</p>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={!!item.featured} onChange={(e) => set({ featured: e.target.checked })} data-testid="toggle-featured" className="mt-1" />
                <span className="text-sm text-[#4d4d4d] leading-snug">Afficher sur l'accueil dans « Là où nous vous emmenons » <span className="block text-[11px] text-[#737373] mt-0.5">Visible uniquement une fois le contenu publié.</span></span>
              </label>
            </div>

            <div className="bg-white border border-[#EAE8E3] p-6 space-y-2">
              <p className="label-eyebrow">URL publique</p>
              <p className="text-sm break-all text-[#1A1B18]">/{prefix}/{item.slug}</p>
              {item.status === "published" && <a href={`/${prefix}/${item.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-[#96836B] hover:text-[#1A1B18]"><ExternalLink className="h-3.5 w-3.5" /> Ouvrir</a>}
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}

const Field = ({ label, help, children }) => (
  <div className="space-y-1.5">
    <Label className="label-eyebrow">{label}</Label>
    {children}
    {help && <p className="text-[10px] text-[#737373]">{help}</p>}
  </div>
);

const ParentPicker = ({ item, set }) => {
  const [destinations, setDestinations] = useState([]);
  useEffect(() => {
    api.get("/admin/content", { params: { content_type: "destination" } })
      .then((r) => setDestinations(r.data || []))
      .catch(() => setDestinations([]));
  }, []);
  return (
    <div className="bg-white border border-[#EAE8E3] p-6 space-y-2" data-testid="parent-picker">
      <Label className="label-eyebrow">Destination pilier</Label>
      <select
        value={item.parent_id || ""}
        onChange={(e) => set({ parent_id: e.target.value || null })}
        data-testid="parent-select"
        className="w-full border border-[#EAE8E3] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#1A1B18]"
      >
        <option value="">— Rattachement automatique (par le nom) —</option>
        {destinations.map((d) => (
          <option key={d.id} value={d.id}>{d.title}{d.status !== "published" ? " (brouillon)" : ""}</option>
        ))}
      </select>
      <p className="text-[10px] text-[#737373]">Rattache ce contenu à une destination pilier. Il apparaîtra dans le cluster de cette destination. Laisser vide = rattachement automatique par correspondance de nom.</p>
    </div>
  );
};

const CoverUploader = ({ item, set }) => {
  const [up, setUp] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setUp(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/content/media/upload?alt=${encodeURIComponent(item.title || "")}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      set({ cover_image: data.url, cover_alt: item.cover_alt || item.title });
      toast.success("Image importée (WebP).");
    } catch (e) { toast.error(e?.response?.data?.detail || "Import impossible."); }
    finally { setUp(false); }
  };
  return (
    <div className="space-y-2">
      <Label className="label-eyebrow">Image principale</Label>
      {item.cover_image ? (
        <div className="relative w-full aspect-[16/9] border border-[#EAE8E3] overflow-hidden bg-[#1A1B18]">
          <img src={item.cover_image} alt={item.cover_alt || ""} className="w-full h-full object-cover" data-testid="content-cover-preview" />
          <button onClick={() => set({ cover_image: null })} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-[#1A1B18]/85 text-white flex items-center justify-center hover:bg-[#1A1B18]" data-testid="content-cover-remove">×</button>
        </div>
      ) : (
        <div className="w-full aspect-[16/9] border border-dashed border-[#EAE8E3] flex items-center justify-center text-[#96836B] bg-white"><Upload className="h-6 w-6" strokeWidth={1.4} /></div>
      )}
      <div className="flex items-center gap-2">
        <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" disabled={up} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="content-cover-upload"
          className="text-xs file:mr-2 file:px-3 file:py-1.5 file:border file:border-[#1A1B18] file:bg-white hover:file:bg-[#1A1B18] hover:file:text-white file:cursor-pointer file:text-[10px] file:uppercase file:tracking-wider file:rounded-none" />
        {up && <Loader2 className="h-4 w-4 animate-spin text-[#96836B]" />}
      </div>
      <Input value={item.cover_alt || ""} onChange={(e) => set({ cover_alt: e.target.value })} className="luxury-input" placeholder="Texte ALT (accessibilité + SEO)" data-testid="content-cover-alt" />
    </div>
  );
};

const SectionEditor = ({ section, onChange }) => {
  const type = section.type || "richtext";
  return (
    <div className="bg-white border border-[#EAE8E3] p-5 space-y-3" data-testid={`section-${section.key}`}>
      <div className="flex items-center gap-3">
        <input value={section.title || ""} onChange={(e) => onChange({ title: e.target.value })} className="font-serif-display text-lg bg-transparent border-none outline-none flex-1" placeholder="Titre de la section" />
        <span className="text-[9px] uppercase tracking-wider text-[#96836B] border border-[#EAE8E3] px-2 py-0.5">{type}</span>
        <label className="flex items-center gap-1.5 text-[10px] text-[#737373]"><input type="checkbox" checked={section.visible !== false} onChange={(e) => onChange({ visible: e.target.checked })} /> visible</label>
      </div>
      {type === "faq" ? (
        <FaqEditor items={section.items || []} onChange={(items) => onChange({ items })} />
      ) : type === "profile" ? (
        <div className="space-y-3">
          <p className="text-[11px] text-[#737373] leading-relaxed">
            Bloc profil (SEO/GEO). Activez la case <strong>visible</strong> pour l'afficher sur la page. Décrivez pourquoi cette destination convient à ce profil, ajoutez éventuellement une FAQ dédiée. Un CTA « Créer mon voyage » est ajouté automatiquement.
          </p>
          <Textarea value={section.body || ""} onChange={(e) => onChange({ body: e.target.value })} className="luxury-input min-h-[120px]" placeholder="Pourquoi cette destination est idéale pour ce profil…" data-testid={`profile-body-${section.key}`} />
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-[#96836B]">FAQ dédiée (optionnel)</Label>
            <div className="mt-2"><FaqEditor items={section.items || []} onChange={(items) => onChange({ items })} /></div>
          </div>
          <Input value={section.config?.keywords || ""} onChange={(e) => onChange({ config: { ...section.config, keywords: e.target.value } })} className="luxury-input" placeholder="Mots-clés SEO ciblés (ex. Bali en famille, activités enfants Bali)" data-testid={`profile-keywords-${section.key}`} />
        </div>
      ) : type === "cta" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Textarea value={section.body || ""} onChange={(e) => onChange({ body: e.target.value })} className="luxury-input min-h-[60px] sm:col-span-2" placeholder="Texte d'accroche" />
          <Input value={section.config?.button_label || ""} onChange={(e) => onChange({ config: { ...section.config, button_label: e.target.value } })} className="luxury-input" placeholder="Libellé du bouton (ex. Demander un devis)" />
          <Input value={section.config?.href || ""} onChange={(e) => onChange({ config: { ...section.config, href: e.target.value } })} className="luxury-input" placeholder="Lien (ex. /#devis)" />
        </div>
      ) : type === "list" ? (
        <Textarea value={section.body || ""} onChange={(e) => onChange({ body: e.target.value })} className="luxury-input min-h-[100px]" placeholder="Une ligne = un élément de liste" />
      ) : (
        <Textarea value={section.body || ""} onChange={(e) => onChange({ body: e.target.value })} className="luxury-input min-h-[140px]" placeholder="Rédigez le contenu ici. Le HTML/markdown simple est conservé." />
      )}
    </div>
  );
};

const FaqEditor = ({ items, onChange }) => (
  <div className="space-y-3">
    {items.map((qa, i) => (
      <div key={i} className="border border-[#F2EFE9] p-3 space-y-2 relative">
        <Input value={qa.question || ""} onChange={(e) => onChange(items.map((x, j) => j === i ? { ...x, question: e.target.value } : x))} className="luxury-input" placeholder="Question" />
        <Textarea value={qa.answer || ""} onChange={(e) => onChange(items.map((x, j) => j === i ? { ...x, answer: e.target.value } : x))} className="luxury-input min-h-[60px]" placeholder="Réponse" />
        <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="absolute top-2 right-2 text-[#737373] hover:text-[#7A4B3B]"><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
    ))}
    <Button onClick={() => onChange([...items, { question: "", answer: "" }])} variant="outline" className="rounded-none text-[10px] uppercase tracking-[0.2em]" data-testid="add-faq"><Plus className="h-3 w-3 mr-1.5" /> Ajouter une question</Button>
  </div>
);

const SeoPanel = ({ item, set, prefix }) => {
  const seo = item.seo || {};
  const s = (patch) => set({ seo: { ...seo, ...patch } });
  const title = seo.meta_title || item.title || "Titre de la page";
  const desc = seo.meta_description || item.excerpt || "Description de la page…";
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4">
        <Field label="Meta title"><Input value={seo.meta_title || ""} onChange={(e) => s({ meta_title: e.target.value })} className="luxury-input" data-testid="seo-meta-title" /></Field>
        <Field label="Meta description"><Textarea value={seo.meta_description || ""} onChange={(e) => s({ meta_description: e.target.value })} className="luxury-input min-h-[70px]" data-testid="seo-meta-desc" /></Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Slug (URL)"><Input value={item.slug || ""} onChange={(e) => set({ slug: e.target.value })} className="luxury-input" data-testid="seo-slug" /></Field>
          <Field label="URL canonique"><Input value={seo.canonical_url || ""} onChange={(e) => s({ canonical_url: e.target.value })} className="luxury-input" placeholder="Laisser vide = auto" /></Field>
          <Field label="Mot-clé principal"><Input value={seo.primary_keyword || ""} onChange={(e) => s({ primary_keyword: e.target.value })} className="luxury-input" data-testid="seo-primary-kw" /></Field>
          <Field label="Mots-clés secondaires"><Input value={(seo.secondary_keywords || []).join(", ")} onChange={(e) => s({ secondary_keywords: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} className="luxury-input" placeholder="séparés par des virgules" /></Field>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!seo.noindex} onChange={(e) => s({ noindex: e.target.checked, robots: e.target.checked ? "noindex, nofollow" : "index, follow" })} /> Ne pas indexer (noindex)</label>
      </div>

      {/* Google preview */}
      <div>
        <p className="label-eyebrow mb-3">Aperçu Google</p>
        <div className="bg-white border border-[#EAE8E3] p-4 max-w-xl" data-testid="google-preview">
          <p className="text-xs text-[#202124]">dealtravel.eu › {prefix} › {item.slug}</p>
          <p className="text-[#1a0dab] text-lg leading-tight truncate">{title}</p>
          <p className="text-sm text-[#4d5156] line-clamp-2">{desc}</p>
        </div>
      </div>

      {/* Open Graph preview */}
      <div>
        <p className="label-eyebrow mb-3">Aperçu Open Graph (réseaux sociaux)</p>
        <div className="bg-white border border-[#EAE8E3] max-w-md overflow-hidden" data-testid="og-preview">
          {item.cover_image ? <img src={item.cover_image} alt="" className="w-full aspect-[1.91/1] object-cover" /> : <div className="w-full aspect-[1.91/1] bg-[#F2EFE9]" />}
          <div className="p-3">
            <p className="text-[11px] uppercase tracking-wide text-[#737373]">dealtravel.eu</p>
            <p className="font-medium truncate">{seo.og_title || title}</p>
            <p className="text-sm text-[#737373] line-clamp-2">{seo.og_description || desc}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const MeshPanel = ({ id, item, set }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const links = item.internal_links || [];
  const loadSuggestions = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/content/${id}/suggest-links`); setSuggestions(data); }
    catch { toast.error("Impossible de générer des suggestions."); }
    finally { setLoading(false); }
  };
  const addLink = (s) => {
    if (links.some((l) => l.to_id === s.to_id)) return;
    set({ internal_links: [...links, { to_id: s.to_id, label: s.title, relation: s.relation || "related", source: "auto" }] });
    setSuggestions((cur) => cur.filter((x) => x.to_id !== s.to_id));
    toast.success("Lien ajouté.");
  };
  const removeLink = (to_id) => set({ internal_links: links.filter((l) => l.to_id !== to_id) });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div>
        <p className="label-eyebrow mb-3">Liens internes ({links.length})</p>
        {links.length === 0 ? <p className="text-sm text-[#737373]">Aucun lien. Ajoutez-en depuis les suggestions →</p> : (
          <div className="space-y-2" data-testid="mesh-links">
            {links.map((l) => (
              <div key={l.to_id} className="flex items-center gap-2 bg-white border border-[#EAE8E3] p-3">
                <Link2 className="h-4 w-4 text-[#96836B]" />
                <span className="flex-1 text-sm truncate">{l.label || l.to_id}</span>
                <span className="text-[9px] uppercase tracking-wider text-[#737373]">{l.relation}</span>
                <button onClick={() => removeLink(l.to_id)} className="text-[#737373] hover:text-[#7A4B3B]"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="label-eyebrow">Suggestions automatiques</p>
          <Button onClick={loadSuggestions} disabled={loading} variant="outline" className="rounded-none text-[10px] uppercase tracking-[0.2em]" data-testid="btn-suggest-links">
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1.5" />} Générer
          </Button>
        </div>
        {suggestions.length === 0 ? <p className="text-sm text-[#737373]">Cliquez sur « Générer » pour proposer des liens pertinents.</p> : (
          <div className="space-y-2" data-testid="mesh-suggestions">
            {suggestions.map((s) => (
              <button key={s.to_id} onClick={() => addLink(s)} className="w-full flex items-center gap-2 bg-white border border-[#EAE8E3] p-3 hover:border-[#96836B] text-left">
                <Plus className="h-4 w-4 text-[#96836B]" />
                <span className="flex-1 text-sm truncate">{s.title}</span>
                <span className="text-[9px] uppercase tracking-wider text-[#737373]">{s.content_type}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const VersionsPanel = ({ id, onRestored }) => {
  const [versions, setVersions] = useState([]);
  const [open, setOpen] = useState(false);
  const load = async () => { try { const { data } = await api.get(`/admin/content/${id}/versions`); setVersions(data); setOpen(true); } catch { toast.error("Erreur."); } };
  const restore = async (vid) => {
    if (!window.confirm("Restaurer cette version ? La version actuelle sera sauvegardée.")) return;
    try { const { data } = await api.post(`/admin/content/${id}/versions/${vid}/restore`); onRestored(data); toast.success("Version restaurée."); load(); }
    catch { toast.error("Restauration impossible."); }
  };
  return (
    <div className="bg-white border border-[#EAE8E3] p-6">
      <div className="flex items-center justify-between">
        <p className="label-eyebrow">Historique des versions</p>
        <Button onClick={load} variant="outline" className="rounded-none text-[10px] uppercase tracking-[0.2em]" data-testid="btn-load-versions"><History className="h-3.5 w-3.5 mr-1.5" /> Charger</Button>
      </div>
      {open && (
        <div className="mt-4 divide-y divide-[#F2EFE9]" data-testid="versions-list">
          {versions.length === 0 ? <p className="text-sm text-[#737373]">Aucune version enregistrée.</p> : versions.map((v) => (
            <div key={v.id} className="flex items-center justify-between py-2.5 text-sm">
              <span className="text-[#737373]">{format(parseISO(v.created_at), "d MMM yyyy · HH:mm:ss", { locale: fr })} {v.note ? `(${v.note})` : ""}</span>
              <button onClick={() => restore(v.id)} className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-[#96836B] hover:text-[#1A1B18]"><RotateCcw className="h-3.5 w-3.5" /> Restaurer</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ScheduleControl = ({ current, onSchedule }) => {
  const [dt, setDt] = useState(current ? current.slice(0, 16) : "");
  return (
    <div className="flex items-center gap-2">
      <input type="datetime-local" value={dt} onChange={(e) => setDt(e.target.value)} className="text-sm border border-[#EAE8E3] px-2 py-1.5 flex-1 bg-white" data-testid="schedule-input" />
      <Button onClick={() => dt && onSchedule(new Date(dt).toISOString())} disabled={!dt} variant="outline" className="rounded-none text-[10px] uppercase tracking-[0.2em]" data-testid="btn-schedule">OK</Button>
    </div>
  );
};
